import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { ObjectSchema, AnyObject } from "yup";

/**
 * Builds a Yup schema bound to the active i18n locale.
 *
 * Why a hook (not a module-level schema): Yup messages are captured by reference
 * at schema-construction time. If you build the schema once at module scope using
 * `t("validation.required")`, switching locales mid-session leaves the captured
 * messages stale. `useValidationSchema` rebuilds the schema whenever the active
 * language changes, so messages render in the correct locale.
 *
 * Pattern: pass a `(t) => yup.object({...})` builder. The builder receives the
 * live `t` function and returns the schema. Memoized on the active language.
 *
 * @example
 *   const schema = useValidationSchema((t) =>
 *     yup.object({
 *       name: yup.string().required(t("validation:rename.name_required")),
 *     })
 *   );
 *
 *   const { register, handleSubmit, formState } = useForm({
 *     resolver: yupResolver(schema),
 *   });
 */
export const useValidationSchema = <T extends AnyObject>(builder: (t: TFunction) => ObjectSchema<T>): ObjectSchema<T> => {
  const { t, i18n } = useTranslation();

  return useMemo(() => builder(t), [i18n.language]);
};
